import { auteurs, conjointsDe, enfantsDe, parentsDe } from './modele.js';
import type { DonneesArbre } from './types.js';

/**
 * Mise en page du vrai arbre généalogique (PARITE-2) : fonction pure des données du dossier. Les auteurs décédés sont sur
 * la première rangée ; chaque couple est côte à côte ; chaque enfant est relié à ses parents par un lien parent → enfant.
 * Une personne que l'arbre n'atteint pas reste visible sur une dernière rangée, signalée `sansLien`.
 */
export interface Taille {
  largeur: number;
  hauteur: number;
}
export interface Point {
  x: number;
  y: number;
}
export interface NoeudPlace {
  id: string;
  x: number;
  y: number;
  rang: number;
  sansLien?: true;
}
export interface LienPlace {
  type: 'filiation' | 'union';
  de: string;
  vers: string;
  parents: string[];
  depuis: Point;
  a: Point;
  chemin: string;
  /** Lien d'un parent hors du couple d'où l'enfant est dessiné : tracé en pointillé. */
  secondaire?: true;
}
export interface MiseEnPage {
  largeur: number;
  hauteur: number;
  noeuds: NoeudPlace[];
  liens: LienPlace[];
}

export const ECART_X = 24;
export const ECART_Y = 56;
export const ECART_COUPLE = 16;

interface Unite {
  membres: string[];
  enfants: Unite[];
  largeurPropre: number;
  largeurSous: number;
}

const n = (v: number): string => String(Math.round(v * 100) / 100);

export function mettreEnPage(d: DonneesArbre, taille: Taille): MiseEnPage {
  const existe = new Set(d.personnes.map((p) => p.id));
  const dans = (ids: string[]): string[] => ids.filter((id) => existe.has(id));
  const places = new Set<string>();
  const sommeLargeur = (k: number): number => k * taille.largeur + (k - 1) * ECART_COUPLE;

  // Parents d'une fratrie sans union saisie (parents « à rechercher », ADR 0064) : côte à côte, sans trait d'union.
  const sansUnion = new Set<string>();
  const coParentsDe = (id: string): string[] =>
    dans([...new Set(enfantsDe(d, id).flatMap((e) => parentsDe(d, e)))]).filter(
      (p) => p !== id && parentsDe(d, p).length === 0 && !conjointsDe(d, id).includes(p),
    );

  function construire(id: string): Unite {
    places.add(id);
    const membres = [id, ...dans(conjointsDe(d, id)).filter((c) => !places.has(c))];
    for (const c of coParentsDe(id)) {
      if (places.has(c) || membres.includes(c)) continue;
      membres.push(c);
      sansUnion.add(c);
    }
    for (const m of membres) places.add(m);
    // Frères et sœurs dans l'ordre des naissances connues ; les dates inconnues après, dans l'ordre du dossier.
    const ids = [...new Set(membres.flatMap((m) => dans(enfantsDe(d, m))))];
    const naissance = (e: string): string | null => d.personnes.find((p) => p.id === e)?.birthDate?.value ?? null;
    const tries = ids
      .map((e, i) => ({ e, i, n: naissance(e) }))
      .sort((a, b) => (a.n === null ? 1 : 0) - (b.n === null ? 1 : 0) || (a.n ?? '').localeCompare(b.n ?? '') || a.i - b.i)
      .map((x) => x.e);
    const enfants: Unite[] = [];
    for (const e of tries) if (!places.has(e)) enfants.push(construire(e));
    const largeurPropre = sommeLargeur(membres.length);
    const sous = enfants.reduce((s, u) => s + u.largeurSous, 0) + Math.max(0, enfants.length - 1) * ECART_X;
    return { membres, enfants, largeurPropre, largeurSous: Math.max(largeurPropre, sous) };
  }

  const aDesRelations = (id: string): boolean => d.relations.some((r) => r.personAId === id || r.personBId === id);
  const racines: Unite[] = [];
  for (const id of dans(auteurs(d))) if (!places.has(id) && aDesRelations(id)) racines.push(construire(id));

  const noeuds: NoeudPlace[] = [];
  const liens: LienPlace[] = [];
  const centreDe = new Map<string, number>();
  const rangDe = new Map<string, number>();
  const ancres = new Map<Unite, Point>();
  let rangMax = 0;

  function poser(u: Unite, x0: number, rang: number): void {
    rangMax = Math.max(rangMax, rang);
    const y = rang * (taille.hauteur + ECART_Y);
    const debut = x0 + (u.largeurSous - u.largeurPropre) / 2;
    u.membres.forEach((id, i) => {
      const x = debut + i * (taille.largeur + ECART_COUPLE);
      noeuds.push({ id, x, y, rang });
      centreDe.set(id, x + taille.largeur / 2);
      rangDe.set(id, rang);
    });
    const centres = u.membres.map((m) => centreDe.get(m) ?? 0);
    ancres.set(u, { x: centres.reduce((s, c) => s + c, 0) / centres.length, y: y + taille.hauteur });
    u.membres.slice(1).forEach((m, i) => {
      if (sansUnion.has(m)) return;
      const gauche = u.membres[i] as string;
      const yc = y + taille.hauteur / 2;
      const x1 = (centreDe.get(gauche) ?? 0) + taille.largeur / 2;
      const x2 = (centreDe.get(m) ?? 0) - taille.largeur / 2;
      liens.push({
        type: 'union',
        de: gauche,
        vers: m,
        parents: [gauche, m],
        depuis: { x: x1, y: yc },
        a: { x: x2, y: yc },
        chemin: `M ${n(x1)} ${n(yc)} H ${n(x2)}`,
      });
    });
    const totalEnfants = u.enfants.reduce((s, e) => s + e.largeurSous, 0) + Math.max(0, u.enfants.length - 1) * ECART_X;
    let x = x0 + (u.largeurSous - totalEnfants) / 2;
    for (const e of u.enfants) {
      poser(e, x, rang + 1);
      x += e.largeurSous + ECART_X;
    }
  }

  let x0 = 0;
  for (const r of racines) {
    poser(r, x0, 0);
    x0 += r.largeurSous + ECART_X;
  }
  let largeur = Math.max(0, x0 - ECART_X);

  // Liens parent → enfant : un par enfant, parti du milieu du couple, par un trait horizontal commun, jusqu'au haut de l'enfant.
  const lienFiliation = (u: Unite, enfant: string, parents: string[], secondaire: boolean): void => {
    const ancre = ancres.get(u);
    const cx = centreDe.get(enfant);
    const ye = (rangDe.get(enfant) ?? 0) * (taille.hauteur + ECART_Y);
    if (!ancre || cx === undefined) return;
    const bus = ancre.y + ECART_Y / 2;
    liens.push({
      type: 'filiation',
      de: parents[0] as string,
      vers: enfant,
      parents,
      depuis: ancre,
      a: { x: cx, y: ye },
      chemin: `M ${n(ancre.x)} ${n(ancre.y)} V ${n(bus)} H ${n(cx)} V ${n(ye)}`,
      ...(secondaire ? { secondaire: true as const } : {}),
    });
  };
  const unites: Unite[] = [];
  const collecter = (u: Unite): void => {
    unites.push(u);
    u.enfants.forEach(collecter);
  };
  racines.forEach(collecter);
  for (const u of unites) {
    for (const e of u.enfants) {
      const tete = e.membres[0] as string;
      lienFiliation(
        u,
        tete,
        dans(parentsDe(d, tete)).filter((p) => u.membres.includes(p)),
        false,
      );
    }
  }
  // Un parent hors du couple d'où l'enfant est dessiné (ex. conjoint ayant lui-même des parents dans l'arbre).
  for (const u of unites) {
    for (const m of u.membres) {
      for (const e of dans(enfantsDe(d, m))) {
        const tete = unites.find((x) => x.enfants.some((c) => c.membres[0] === e));
        if (
          rangDe.has(e) &&
          tete &&
          tete !== u &&
          !tete.membres.includes(m) &&
          !liens.some((l) => l.type === 'filiation' && l.vers === e && l.parents.includes(m))
        ) {
          lienFiliation(u, e, [m], true);
        }
      }
    }
  }

  // Personnes que l'arbre n'atteint pas : une rangée à part, jamais omises.
  const restantes = d.personnes.filter((p) => !places.has(p.id));
  if (restantes.length > 0) {
    const rang = noeuds.length > 0 ? rangMax + 1 : 0;
    const y = rang * (taille.hauteur + ECART_Y);
    restantes.forEach((p, i) => {
      const x = i * (taille.largeur + ECART_X);
      noeuds.push({ id: p.id, x, y, rang, sansLien: true });
    });
    rangMax = Math.max(rangMax, rang);
    largeur = Math.max(largeur, restantes.length * taille.largeur + (restantes.length - 1) * ECART_X);
  }

  return {
    largeur,
    hauteur: noeuds.length === 0 ? 0 : rangMax * (taille.hauteur + ECART_Y) + taille.hauteur,
    noeuds,
    liens,
  };
}
